import merchantConfig from "../config.json";

export const HUBSPOT_MEETING_URL = "https://meetings-eu1.hubspot.com/nzerbib";

export const HUBSPOT_MEETING_TARGETS = {
  DEFAULT: "default",
  ONBOARDING_DEMO: "onboardingDemo",
  ONBOARDING_AGENCY_DEMO: "onboardingAgencyDemo",
  PAYWALL_DEMO: "paywallDemo",
} as const;

export type HubspotMeetingTarget =
  (typeof HUBSPOT_MEETING_TARGETS)[keyof typeof HUBSPOT_MEETING_TARGETS];

const HUBSPOT_FALLBACK_MEETING_URL_BY_TARGET = {
  [HUBSPOT_MEETING_TARGETS.DEFAULT]: HUBSPOT_MEETING_URL,
  [HUBSPOT_MEETING_TARGETS.ONBOARDING_DEMO]: HUBSPOT_MEETING_URL,
  [HUBSPOT_MEETING_TARGETS.ONBOARDING_AGENCY_DEMO]: HUBSPOT_MEETING_URL,
  [HUBSPOT_MEETING_TARGETS.PAYWALL_DEMO]: HUBSPOT_MEETING_URL,
} satisfies Record<HubspotMeetingTarget, string>;

const isDevelopment = process.env.NODE_ENV === "development";

const hubspotMeetingUrlsFromConfig = (
  merchantConfig as {
    hubspotMeetingUrls?: Partial<Record<HubspotMeetingTarget, string>>;
  }
).hubspotMeetingUrls;

const splitFullName = (fullName: string | undefined) => {
  const [firstName = "", ...lastNameParts] = (fullName ?? "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  return {
    firstName,
    lastName: lastNameParts.join(" "),
  };
};

const COMPANY_NAME_EXCLUDED_SUBDOMAINS = new Set([
  "www",
  "app",
  "shop",
  "store",
]);

const toCompanyName = (value: string) =>
  value
    .split(/[-_]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const normalizeWebsiteUrl = (websiteUrl: string) =>
  /^https?:\/\//i.test(websiteUrl) ? websiteUrl : `https://${websiteUrl}`;

const deriveCompanyNameFromWebsiteUrl = (websiteUrl: string | undefined) => {
  if (!websiteUrl) {
    return undefined;
  }

  try {
    const hostname = new URL(
      normalizeWebsiteUrl(websiteUrl.trim()),
    ).hostname.toLowerCase();
    const parts = hostname.split(".").filter(Boolean);

    if (parts.length === 0) {
      return undefined;
    }

    const candidate =
      parts.length >= 3 && COMPANY_NAME_EXCLUDED_SUBDOMAINS.has(parts[0])
        ? parts[1]
        : parts[0];

    return candidate ? toCompanyName(candidate) : undefined;
  } catch {
    return undefined;
  }
};

export const getHubspotMeetingBaseUrl = (
  target: HubspotMeetingTarget = HUBSPOT_MEETING_TARGETS.DEFAULT,
) =>
  isDevelopment
    ? HUBSPOT_FALLBACK_MEETING_URL_BY_TARGET[target]
    : (hubspotMeetingUrlsFromConfig?.[target] ??
      HUBSPOT_FALLBACK_MEETING_URL_BY_TARGET[target]);

export const getHubspotMeetingUrl = ({
  email,
  name,
  websiteUrl,
  target = HUBSPOT_MEETING_TARGETS.DEFAULT,
  extraParams,
}: {
  email?: string;
  name?: string;
  websiteUrl?: string;
  target?: HubspotMeetingTarget;
  extraParams?: Record<string, string | number | boolean | null | undefined>;
}) => {
  const { firstName, lastName } = splitFullName(name);
  const companyName = deriveCompanyNameFromWebsiteUrl(websiteUrl);
  const params = new URLSearchParams({
    embed: "true",
    email: email ?? "",
    firstname: firstName,
    lastname: lastName,
    company: companyName ?? "",
  });

  Object.entries(extraParams ?? {}).forEach(([key, value]) => {
    if (value === null || value === undefined || value === "") {
      return;
    }

    params.set(key, String(value));
  });

  return `${getHubspotMeetingBaseUrl(target)}?${params.toString()}`;
};
